import * as React from 'react';
import { Spin } from 'antd';
import './busy-indicator.css';

// ═══════════════════════════════════════════════════════════════════════════════
// THE STANDARD BUSY INDICATOR.
//
// One drawing, three doors:
//
//   <PageLoader label hint/>   a page or panel that has nothing to show yet.
//                              Centred, on-brand, and it SAYS what is loading —
//                              a busy page must never leave the reader guessing
//                              whether it is busy or broken.
//   <Spin/> (every one)        installBusyIndicator() makes the compact mark the
//                              antd default, so ~800 existing <Spin spinning>
//                              overlays change look without changing code.
//   <BusyMark/>                the bare mark, for a composed panel that brings
//                              its own words (ClinicianBusyPanel).
//
// Started life as Merchant.Web/programs/shared/page-loader.comp.tsx, which
// replaced a bare 16px <Spin/> in the top-left of a padded page — at that size
// and position it read as a rendering glitch rather than work in progress.
//
// Do NOT add a fourth spinner. Skeleton (known layout, content arriving) and a
// Button's `loading` prop (the action itself is busy) are the only other
// sanctioned "working" states; see docs/claude/frontend-details.md.
// ═══════════════════════════════════════════════════════════════════════════════

export interface BusyMarkProps {
  /** `full` (78px) under a label; `compact` (36px) inside antd Spin. */
  size?: 'full' | 'compact';
  /** Override the mark colour for this instance only; otherwise --mc-busy-color. */
  color?: string;
  className?: string;
  style?: React.CSSProperties;
  /** antd clones its indicator with `percent`; accepted so it never reaches the DOM. */
  percent?: unknown;
}

export const BusyMark: React.FC<BusyMarkProps> = ({ size = 'full', color, className, style }) => (
  <span
    className={['mc-busy-dot', className].filter(Boolean).join(' ')}
    style={style}
    aria-hidden="true"
  >
    <span
      className={`mc-busy-mark${size === 'compact' ? ' mc-busy-mark--compact' : ''}`}
      style={color ? { color } : undefined}
    >
      <span className="mc-busy-ring" />
      <span className="mc-busy-ring mc-busy-ring--inner" />
      <span className="mc-busy-core" />
    </span>
  </span>
);

export interface PageLoaderProps {
  /** What is being loaded, as a short sentence. Always say it — "Loading" alone is the fallback, not the goal. */
  label?: string;
  /** Why it takes a moment. Shown under the label; omit when it would be noise. */
  hint?: string;
  /** `content` fills the page body (58vh); `viewport` is for the root Suspense fallback before any shell exists. */
  fill?: 'content' | 'viewport';
  className?: string;
}

export const PageLoader: React.FC<PageLoaderProps> = ({ label = 'Loading', hint, fill = 'content', className }) => (
  <div
    className={['mc-busy-page', fill === 'viewport' ? 'mc-busy-page--viewport' : '', className].filter(Boolean).join(' ')}
    role="status"
    aria-live="polite"
    aria-busy="true"
  >
    <BusyMark size="full" />
    <div>
      <div className="mc-busy-label">{label}</div>
      {hint && <div className="mc-busy-hint">{hint}</div>}
    </div>
  </div>
);

/**
 * The element antd <Spin> renders as its indicator. One shared instance: hand it
 * to `ConfigProvider spin={{ indicator: BUSY_SPIN_INDICATOR }}` in the app shell
 * (that prop wins over the default set below, so both must agree).
 */
export const BUSY_SPIN_INDICATOR = <BusyMark size="compact" />;

/**
 * Make the compact mark the default indicator of every antd <Spin> in the app,
 * and (optionally) re-point the mark colour — e.g. at the tenant's brand colour.
 *
 * Call once at module load of the app shell, and again when the brand colour is
 * known. Idempotent. The colour lives in a CSS variable so PageLoader and
 * ClinicianBusyPanel pick it up too — one call, every door.
 */
export function installBusyIndicator(color?: string): void {
  if (color && typeof document !== 'undefined') {
    document.documentElement.style.setProperty('--mc-busy-color', color);
  }
  Spin.setDefaultIndicator(BUSY_SPIN_INDICATOR);
}

export default PageLoader;
